import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator'
import BootstrapTable from 'react-bootstrap-table-next'
import Card from 'react-bootstrap/Card'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import Spinner from './Spinner'
import artifact from '../assets/artifact.svg'
import classNames from 'classnames'
import creature from '../assets/creature.svg'
import enchantment from '../assets/enchantment.svg'
import instant from '../assets/instant.svg'
import planeswalker from '../assets/planeswalker.svg'
import salt_48 from '../assets/salt_48.png'
import sorcery from '../assets/sorcery.svg'
import styles from '../styles/DecksTable.module.scss'
import useDarkMode from 'use-dark-mode'
import { useSelector } from 'react-redux'
import { useWindowWidth } from '@react-hook/window-size/throttled'
import { v4 } from 'uuid'

const { SearchBar } = Search

const customTotal = (from, to, size) => (
  <span className='react-bootstrap-table-pagination-total'>
    Showing {from} to {to} of {size} decks
  </span>
)

const sizePerPageRenderer = ({
  options,
  currSizePerPage,
  onSizePerPageChange,
}) => (
  <div className='btn-group' role='group'>
    {options.map((option) => {
      const isSelect = currSizePerPage === `${option.page}`
      return (
        <button
          key={option.text}
          type='button'
          onClick={() => onSizePerPageChange(option.page)}
          className={`btn ${isSelect ? 'btn-primary' : 'btn-secondary'}`}
        >
          {option.text}
        </button>
      )
    })}
  </div>
)

const paginationOptions = {
  custom: true,
  disablePageTitle: true,
  firstPageTitle: 'Next page',
  nextPageText: 'Next',
  nextPageTitle: 'First page',
  paginationTotalRenderer: customTotal,
  prePageText: 'Back',
  prePageTitle: 'Pre page',
  showTotal: true,
  sizePerPageList: [
    {
      text: '10',
      value: 10,
    },
    {
      text: '25',
      value: 25,
    },
    {
      text: '50',
      value: 50,
    },
    {
      text: '100',
      value: 100,
    },
  ],
  sizePerPageRenderer,
  withFirstAndLast: false,
}

const urlhashFormatter = (cell, row) => (
  <Link to={`/deckpreview/${cell}`}>View Decklist</Link>
)
const priceFormatter = (cell, row) => `$${cell}`
const saltFormatter = (cell, row) => (
  <span>
    <img
      alt='Salt Score'
      className={styles.image}
      src={salt_48}
      title='Salt Score'
    />
    {cell.toFixed(2)}
  </span>
)
const creatureFormatter = (cell, row) => (
  <span>
    <img
      alt='Creature'
      className={styles.image}
      src={creature}
      title='Creature'
    />
    {cell}
  </span>
)
const instantFormatter = (cell, row) => (
  <span>
    <img alt='Instant' className={styles.image} src={instant} title='Instant' />
    {cell}
  </span>
)
const sorceryFormatter = (cell, row) => (
  <span>
    <img alt='Sorcery' className={styles.image} src={sorcery} title='Sorcery' />
    {cell}
  </span>
)
const artifactFormatter = (cell, row) => (
  <span>
    <img
      alt='Artifact'
      className={styles.image}
      src={artifact}
      title='Artifact'
    />
    {cell}
  </span>
)
const enchantmentFormatter = (cell, row) => (
  <span>
    <img
      alt='Enchantment'
      className={styles.image}
      src={enchantment}
      title='Enchantment'
    />
    {cell}
  </span>
)
const planeswalkerFormatter = (cell, row) => (
  <span>
    <img
      alt='Planeswalker'
      className={styles.image}
      src={planeswalker}
      title='Planeswalker'
    />
    {cell}
  </span>
)
const themeTribeFormatter = (cell, row) => (
  <span>
    {[row.tribe, row.theme]
      .filter((item) => item)
      .map((item) => (
        <div key={v4()}>{item}</div>
      ))}
  </span>
)

const columnsMobile = [
  {
    dataField: 'urlhash',
    formatter: urlhashFormatter,
    text: '',
  },
  {
    dataField: 'price',
    formatter: priceFormatter,
    sort: true,
    text: 'Price',
  },
  {
    dataField: 'theme',
    formatter: themeTribeFormatter,
    sort: true,
    text: 'Theme',
  },
  {
    dataField: 'savedate',
    sort: true,
    text: 'Save Date',
  },
]

const columns = [
  {
    dataField: 'urlhash',
    formatter: urlhashFormatter,
    text: '',
  },
  {
    dataField: 'price',
    formatter: priceFormatter,
    sort: true,
    text: 'Price',
  },
  {
    dataField: 'tribe',
    sort: true,
    text: 'Tribe',
  },
  {
    dataField: 'theme',
    sort: true,
    text: 'Theme',
  },
  {
    dataField: 'salt',
    formatter: saltFormatter,
    sort: true,
    text: 'Salt',
  },
  {
    dataField: 'creature',
    formatter: creatureFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'instant',
    formatter: instantFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'sorcery',
    formatter: sorceryFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'artifact',
    formatter: artifactFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'enchantment',
    formatter: enchantmentFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'planeswalker',
    formatter: planeswalkerFormatter,
    sort: true,
    text: '',
  },
  {
    dataField: 'savedate',
    sort: true,
    text: 'Save Date',
  },
]

const DecksTable = ({ rows }) => {
  const darkMode = useDarkMode(false)
  const isLoading = useSelector((state) => state.pagedata.isLoading)
  const windowWidth = useWindowWidth()
  const isDesktop = windowWidth >= 1024 // also hard-coded in Table, ToggleList
  return (
    <Card
      className={classNames(
        styles.container,
        { [styles.dark]: darkMode.value },
        'shadow-sm'
      )}
    >
      <Card.Body>
        {isLoading ? (
          <div className={styles.spinner}>
            <Spinner className='fa-2x' />
          </div>
        ) : (
          rows && (
            <PaginationProvider
              data={rows}
              columns={isDesktop ? columns : columnsMobile}
              keyField='id'
              pagination={paginationFactory(paginationOptions)}
            >
              {({ paginationProps, paginationTableProps }) => (
                <ToolkitProvider
                  keyField='id'
                  data={rows}
                  columns={isDesktop ? columns : columnsMobile}
                  search
                >
                  {(toolkitProps) => (
                    <div className={styles.table}>
                      <div className={classNames(styles.pad, styles.row)}>
                        <SizePerPageDropdownStandalone {...paginationProps} />
                        <SearchBar {...toolkitProps.searchProps} />
                      </div>
                      <BootstrapTable
                        {...toolkitProps.baseProps}
                        {...paginationTableProps}
                        headerWrapperClasses={styles.tableHead}
                        striped
                      />
                      <div className={styles.row}>
                        <PaginationTotalStandalone {...paginationProps} />
                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    </div>
                  )}
                </ToolkitProvider>
              )}
            </PaginationProvider>
          )
        )}
      </Card.Body>
    </Card>
  )
}

DecksTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object.isRequired),
}

export default DecksTable
